// The Quote Brain live report (slice LIVE, backend/src/routes/quote-brain-report.js).
// One account may read it: the server answers 404 to everyone else, so a
// null from fetchQuoteBrainAlerts means "not this user" and the menu entry
// and banner stay hidden.

import { apiFetch } from './data-service-api';

export interface QuoteBrainAlert {
    id: string;
    kind: 'run_failed' | 'cost_over' | 'trigger_failed' | 'digest' | 'day7' | 'day14';
    projectId: string | null;
    runId: string | null;
    subject: string;
    body: string;
    data: Record<string, unknown>;
    emailStatus: 'pending' | 'sending' | 'sent' | 'failed' | 'not_configured';
    emailAttempts: number;
    emailError: string | null;
    emailedAt: string | null;
    acknowledgedAt: string | null;
    createdAt: string;
}

export interface QuoteBrainItemPair {
    draftId: string;
    draftTitle: string;
    sentTitle: string;
    titleSimilarity: number;
    textSimilarity: number;
    draftAmount: number | null;
    sentAmount: number | null;
    priceChange: number | null;
    reworded: boolean;
    repriced: boolean;
}

export interface QuoteBrainComparison {
    basis: 'ex_gst' | 'inc_gst' | 'inc_gst_assumed';
    priceDiffExGst: number | null;
    priceDiffPct: number | null;
    counts: { draftItems: number; sentItems: number; added: number; removed: number; reworded: number; repriced: number; unchanged: number };
    wordingChange: number;
    paired: QuoteBrainItemPair[];
    added: Array<{ title: string; kind: string; amount: number | null }>;
    removed: Array<{ draftId: string; title: string; kind: string; amount: number | null }>;
}

export interface QuoteBrainJob {
    id: string;
    projectId: string;
    projectTitle: string | null;
    triggerSource: string;
    gateMode: 'shadow' | 'on';
    firstRunId: string | null;
    triggerError: string | null;
    startAttempts: number;
    startError: string | null;
    evidenceAtStart: { file?: number; text?: number; missing?: number } | null;
    runs: number;
    failures: number;
    failureReasons: Array<{ runId: string; reason: string | null; at: string }>;
    turns: number;
    wallSeconds: number | null;
    billedUsd: number;
    lastRunStatus: string | null;
    draftOutcome: 'written' | 'held' | 'candidate_only' | null;
    draftSubtotalExGst: number | null;
    draftTotalIncGst: number | null;
    draftAt: string | null;
    sentAt: string | null;
    sentSubtotalExGst: number | null;
    sentTotalIncGst: number | null;
    priceDiffPct: number | null;
    itemsAdded: number | null;
    itemsRemoved: number | null;
    itemsReworded: number | null;
    itemsRepriced: number | null;
    itemsUnchanged: number | null;
    wordingChange: number | null;
    comparison: QuoteBrainComparison | null;
    judgeScores: Record<string, { calibrated?: number; raw?: number } & Record<string, unknown>> | null;
    createdAt: string;
    updatedAt: string;
}

export interface QuoteBrainSummary {
    jobs: number;
    runs: number;
    failures: number;
    notStarted: number;
    waiting: number;
    drafted: number;
    sent: number;
    billedUsd: number;
    costMedian: number | null;
    costP90: number | null;
    timeMedianSec: number | null;
    timeP90Sec: number | null;
    priceDiffMedianPct: number | null;
    priceAbsDiffP90Pct: number | null;
    within5Pct: number;
    within15Pct: number;
    itemsAdded: number;
    itemsRemoved: number;
    itemsReworded: number;
    itemsRepriced: number;
    wordingChangeMedian: number | null;
    judged: number;
}

export interface QuoteBrainReport {
    generatedAt: string;
    liveStart: string | null;
    fortnightEnd: string | null;
    summary: QuoteBrainSummary;
    fortnightSummary: QuoteBrainSummary | null;
    jobs: QuoteBrainJob[];
    alerts: QuoteBrainAlert[];
    mail: { configured: boolean; host: string | null; port: number | null; from: string | null; recipient: string | null };
}

export interface QuoteBrainAlertsResponse {
    alerts: QuoteBrainAlert[];
    mail: { configured: boolean; recipientSet: boolean };
}

export function fetchQuoteBrainReport(): Promise<QuoteBrainReport> {
    return apiFetch('/quote-brain/report');
}

/** The open alerts, or null when this account may not read the report. */
export async function fetchQuoteBrainAlerts(): Promise<QuoteBrainAlertsResponse | null> {
    try {
        return await apiFetch('/quote-brain/report/alerts');
    } catch (err) {
        if ((err as { status?: number })?.status === 404) return null;
        throw err;
    }
}

export function acknowledgeQuoteBrainAlert(id: string): Promise<{ alert: QuoteBrainAlert }> {
    return apiFetch(`/quote-brain/report/alerts/${encodeURIComponent(id)}/ack`, { method: 'POST', body: JSON.stringify({}) });
}
